import { Injectable } from '@angular/core';
import { FormDefinition, FormField, FormFieldDefinition } from '@nuso/forms/data-access';
import {
  RECORDING_PERMISSIONS,
  DEFAULT_RECORDING_ADMIN_PERMISSIONS,
} from '@nuso/iam/types';
import { LANGUAGE_OPTIONS } from '@nuso/forms/util';


export const IAM_APP_USER_DETAILS_FORM_DEF: FormDefinition = {
  name: 'iam-app-user-details-form',
  fields: [
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      validators: { required: true },
    },
    {
      name: 'description',
      label: 'Description',
      type: 'text',
    },
    {
      name: 'language',
      label: 'Language',
      type: 'select',
      options: LANGUAGE_OPTIONS,
      defaultTo: 'en-US'
    },
  ],
};

const RECORDING_ROLE_FIELD: FormFieldDefinition =     {
  name: 'recordingRole',
  label:'Role',
  type: 'select',
  options: [
    { value: 'USER', label: 'User'},
    { value: 'SUPERVISOR', label: 'Supervisor' },
    { value: 'ADMINISTRATOR', label: 'Supervisor+'},
    // Commenting out OWNER for now, as Nigel said it is not needed
    // But I still think we need a concept of full permissions...
    //{ value: 'OWNER', label: 'Owner'},
  ],
  defaultTo: 'USER',
};

export const IAM_CREATE_APP_USER_FORM_DEF: FormDefinition = {
  name: 'iam-create-app-user-form',
  fields: [
    ...IAM_APP_USER_DETAILS_FORM_DEF.fields,
    RECORDING_ROLE_FIELD
  ]
};

export const IAM_RECORDER_PERMISSIONS_FORM_DEF: FormDefinition = {
  name: 'iam-recorder-permissions-form',
  fields: [
    RECORDING_ROLE_FIELD,
    {
      name: 'ownPermissions',
      label: 'Recording Permissions for Owned Media Points:',
      type: 'group',
      fields: [
        {
          name: 'data',
          type: 'row',
          fields: [
            {
              name: RECORDING_PERMISSIONS.List_Own,
              label:'List',
              type: 'checkbox'
            },
            {
              name: RECORDING_PERMISSIONS.View_Own,
              label:'View',
              type: 'checkbox'
            },
            {
              name: RECORDING_PERMISSIONS.Playback_Own,
              label:'Playback',
              type: 'checkbox'
            },
            {
              name: RECORDING_PERMISSIONS.Download_Own,
              label:'Download',
              type: 'checkbox'
            },
            {
              name: RECORDING_PERMISSIONS.Delete_Own,
              label:'Delete',
              type: 'checkbox'
            },
          ]
        }
      ]
    },
    {
      name: 'groupPermissions',
      label: 'Recording Permissions for Media Points in Supervised Groups:',
      type: 'group',
      showIf: {
        recordingRole: {
          operator: 'anyOf',
          value: [ 'SUPERVISOR', 'ADMINISTRATOR', 'OWNER' ],
        },
      },
      fields: [
        {
          name: 'data',
          type: 'row',
          fields: [
            {
              name: RECORDING_PERMISSIONS.List_Group,
              label:'List',
              type: 'checkbox'
            },
            {
              name: RECORDING_PERMISSIONS.View_Group,
              label:'View',
              type: 'checkbox'
            },
            {
              name: RECORDING_PERMISSIONS.Playback_Group,
              label:'Playback',
              type: 'checkbox'
            },
            {
              name: RECORDING_PERMISSIONS.Download_Group,
              label:'Download',
              type: 'checkbox'
            },
            {
              name: RECORDING_PERMISSIONS.Delete_Group,
              label:'Delete',
              type: 'checkbox'
            },
            {
              name: RECORDING_PERMISSIONS.Confidential_Group,
              label:'Access Confidential',
              type: 'checkbox'
            },
          ]
        }
      ]
    },
    {
      name: 'adminPermissions',
      label: 'Recording Permissions for All Media Points:',
      type: 'group',
      showIf: {
        recordingRole: {
          operator: 'anyOf',
          value: [ 'ADMINISTRATOR', 'OWNER' ],
        },
      },
      fields: [
        {
          name: 'data',
          type: 'row',
          fields: [
            {
              name: RECORDING_PERMISSIONS.List_All,
              label:'List',
              type: 'checkbox'
            },
            {
              name: RECORDING_PERMISSIONS.View_All,
              label:'View',
              type: 'checkbox'
            },
            {
              name: RECORDING_PERMISSIONS.Playback_All,
              label:'Playback',
              type: 'checkbox'
            },
            {
              name: RECORDING_PERMISSIONS.Download_All,
              label:'Download',
              type: 'checkbox'
            },
            {
              name: RECORDING_PERMISSIONS.Delete_All,
              label:'Delete',
              type: 'checkbox'
            },
            {
              name: RECORDING_PERMISSIONS.Confidential_All,
              label:'Access Confidential',
              type: 'checkbox'
            },
          ]
        }
      ]
    },
  ]
}


@Injectable({
  providedIn: 'root',
})
export class IamAppUserSharedService {
  defaultPermissions: { ownPermissions: { data: any }, groupPermissions: { data: any }, adminPermissions: { data: any } }
    = { ownPermissions: { data: {} }, groupPermissions: { data: {} }, adminPermissions: { data: {} } };

  constructor() {
    this.setDefaultPermissions();
  }


  private setDefaultPermissions() {
    for (const defaultPermission of DEFAULT_RECORDING_ADMIN_PERMISSIONS) {
      if (defaultPermission.startsWith('recording.own.')) {
        this.defaultPermissions.ownPermissions.data[defaultPermission] = true;
      }
      else if (defaultPermission.startsWith('recording.group.')) {
        this.defaultPermissions.groupPermissions.data[defaultPermission] = true;
      }
      else if (defaultPermission.startsWith('recording.all.')) {
        this.defaultPermissions.adminPermissions.data[defaultPermission] = true;
      }
    }
  }
}
